import React, { useRef, useEffect, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { 
  OrbitControls, 
  useGLTF, 
  useAnimations,
  Html,
  Float,
  Box,
  Sphere,
  ContactShadows,
} from '@react-three/drei';
import * as THREE from 'three';
import ModelAnimationLogger from './ModelAnimationLogger';

// Free 3D Model Sources:
// 1. Mixamo (https://www.mixamo.com) - Free animated humanoid models
// 2. Sketchfab (https://sketchfab.com) - Free CC0 models
// 3. Poly Pizza (https://poly.pizza) - Google Poly archive
// 4. Quaternius (https://quaternius.com) - Free low-poly models
// 5. OpenGameArt (https://opengameart.org) - Free game assets

// Dog Model Component using GLTF
const DogModel = ({ position = [0, 0, 0], rotation = [0, 0, 0], animation = 'idle', scale = 1 }) => {
  const group = useRef();
  const { scene, animations } = useGLTF('/models/ShibaInu.gltf');
  const { actions, names } = useAnimations(animations, group);
  
  // Log available animations on first load
  useEffect(() => {
    if (names.length > 0) {
      console.log('Dog model animations:', names);
    }
  }, [names]);
  
  useEffect(() => {
    // Stop all animations first
    Object.keys(actions).forEach(key => {
      actions[key]?.stop();
    });
    
    // Map animation names to actual model animations
    let animationToPlay = animation;
    
    // Common animation mappings for dog models
    const animationMap = {
      'idle': names.find(n => n.toLowerCase().includes('idle')) || names.find(n => n.toLowerCase().includes('stand')) || names[0],
      'walk': names.find(n => n.toLowerCase().includes('walk')) || names.find(n => n.toLowerCase().includes('trot')) || names.find(n => n.toLowerCase().includes('run')) || names[0],
      'sit': names.find(n => n.toLowerCase().includes('sit')) || names.find(n => n.toLowerCase().includes('sitting')) || names[0],
      'pant': names.find(n => n.toLowerCase().includes('pant') || n.toLowerCase().includes('breath') || n.toLowerCase().includes('idle')) || names[0],
      'lookUp': names.find(n => n.toLowerCase().includes('look') || n.toLowerCase().includes('head')) || names[0],
      'down': names.find(n => n.toLowerCase().includes('down') || n.toLowerCase().includes('lie') || n.toLowerCase().includes('lay')) || names[0]
    };
    
    animationToPlay = animationMap[animation] || names[0];
    
    // Play the selected animation with loop setting
    if (animationToPlay && actions[animationToPlay]) {
      const action = actions[animationToPlay];
      action.reset().fadeIn(0.5).play();
      
      // Set loop behavior based on animation type
      if (animation === 'sit' || animation === 'walk' || animation === 'pant') {
        action.setLoop(THREE.LoopRepeat);
      } else {
        action.setLoop(THREE.LoopOnce);
        action.clampWhenFinished = true;
      }
      
      return () => action?.fadeOut(0.5);
    }
  }, [animation, actions, names]);
  
  useFrame((state) => {
    if (group.current) {
      // Add subtle idle animation
      if (animation === 'idle') {
        group.current.rotation.y = rotation[1] + Math.sin(state.clock.elapsedTime * 0.5) * 0.1;
      }
    }
  });

  return (
    <group ref={group} position={position} rotation={rotation} scale={scale * 0.67}>
      <primitive object={scene} />
    </group>
  );
};

// Human Model Component using GLTF
const HumanModel = ({ position = [0, 0, 0], rotation = [0, 0, 0], animation = 'idle', scale = 1, showHand = false, showTreat = false }) => {
  const group = useRef();
  const { scene, animations } = useGLTF('/models/Character_Male_1.gltf');
  const { actions, names } = useAnimations(animations, group);
  
  // Log available animations on first load
  useEffect(() => {
    if (names.length > 0) {
      console.log('Human model animations:', names);
    }
  }, [names]);
  
  useEffect(() => {
    // Stop all animations first
    Object.keys(actions).forEach(key => {
      actions[key]?.stop();
    });
    
    // Map animation names to actual model animations
    let animationToPlay = animation;
    
    // Common animation mappings for human models
    const animationMap = {
      'idle': names.find(n => n.toLowerCase().includes('idle')) || names.find(n => n.toLowerCase().includes('stand')) || names[0],
      'walk': names.find(n => n.toLowerCase().includes('walk')) || names.find(n => n.toLowerCase().includes('walking')) || names[0],
      'give_treat': names.find(n => n.toLowerCase().includes('give') || n.toLowerCase().includes('feed') || n.toLowerCase().includes('reach')) || names[0],
      'point': names.find(n => n.toLowerCase().includes('point') || n.toLowerCase().includes('gesture')) || names[0],
      'kneel': names.find(n => n.toLowerCase().includes('kneel') || n.toLowerCase().includes('crouch') || n.toLowerCase().includes('squat')) || names.find(n => n.toLowerCase().includes('sit')) || names[0]
    };
    
    animationToPlay = animationMap[animation] || names[0];
    
    // Play the selected animation with loop setting
    if (animationToPlay && actions[animationToPlay]) {
      const action = actions[animationToPlay];
      action.reset().fadeIn(0.5).play();
      
      // Set loop behavior based on animation type
      if (animation === 'sit' || animation === 'walk' || animation === 'pant') {
        action.setLoop(THREE.LoopRepeat);
      } else {
        action.setLoop(THREE.LoopOnce);
        action.clampWhenFinished = true;
      }
      
      return () => action?.fadeOut(0.5);
    }
  }, [animation, actions, names]);

  return (
    <group ref={group} position={position} rotation={rotation} scale={scale}>
      <primitive object={scene} />
      
      {/* Visual indicators for treats/hands */}
      {showTreat && (
        <mesh position={[0.3, 1.5, 0.3]}>
          <boxGeometry args={[0.1, 0.1, 0.1]} />
          <meshStandardMaterial color="#8B4513" />
        </mesh>
      )}
    </group>
  );
};

// Fallback loading component
const LoadingFallback = () => (
  <Html center>
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      <p className="mt-2 text-sm text-gray-600">Loading 3D models...</p>
    </div>
  </Html>
);

// Updated Exercise Scene with GLTF models
const ExerciseSceneWithModels = ({ exerciseName, step, stepText, animationData }) => {
  const { camera, size } = useThree();
  
  useEffect(() => {
    const isMobile = size.width < 640;
    const cameraDistance = isMobile ? 8 : 6;
    const cameraHeight = isMobile ? 5 : 4;
    
    camera.position.set(5, cameraHeight, cameraDistance);
    camera.lookAt(0, 0, 0);
  }, [camera, size]);
  
  const renderFromAnimationData = () => {
    if (!animationData || !animationData.steps || !animationData.steps[step]) {
      return (
        <>
          <DogModel position={[-1, 0, 0]} animation="idle" />
          <HumanModel position={[1.5, 0, 0]} animation="idle" />
        </>
      );
    }
    
    const stepData = animationData.steps[step];
    const { dog, human, extras = {} } = stepData;
    
    return (
      <>
        {/* Dog Model */}
        {dog && (
          <DogModel 
            position={dog.position} 
            rotation={dog.rotation || [0, 0, 0]}
            animation={dog.animation || 'idle'}
            scale={dog.scale || 1}
          />
        )}
        
        {/* Human Model */}
        {human && (
          <HumanModel 
            position={human.position}
            rotation={human.rotation || [0, 0, 0]}
            animation={human.animation || 'idle'}
            scale={human.scale || 1}
            showHand={human.showHand}
            showTreat={human.showTreat}
          />
        )}
        
        {/* Additional scene elements */}
        {extras.sparkles && <Sparkles count={extras.sparkles} />}
        
        {extras.speechBubble && (
          <Float speed={2} floatIntensity={1}>
            <Html position={extras.speechPosition || [0, 2, 0]} center>
              <div className="text-2xl font-bold text-purple-600">{`"${extras.speechBubble}"`}</div>
            </Html>
          </Float>
        )}
        
        {extras.showStopGesture && (
          <Box args={[0.01, 0.8, 0.8]} position={extras.stopGesturePosition || [1.7, 1, 0.4]}>
            <meshStandardMaterial color="#ef4444" opacity={0.5} transparent />
          </Box>
        )}
        
        {/* Step Text */}
        <Html position={[0, -3, 0]} center>
          <div className="text-xs sm:text-sm font-bold text-gray-700 bg-white/95 backdrop-blur-sm rounded-lg px-3 sm:px-6 py-2 shadow-lg max-w-[90vw] sm:max-w-xl lg:max-w-2xl overflow-hidden">
            <div className="truncate sm:whitespace-normal">
              {stepText}
            </div>
          </div>
        </Html>
      </>
    );
  };
  
  return renderFromAnimationData();
};

// Sparkle Effect (copied from original)
const Sparkles = ({ count = 20 }) => {
  const sparklesRef = useRef();
  
  useFrame((state) => {
    if (sparklesRef.current) {
      sparklesRef.current.rotation.y = state.clock.elapsedTime * 0.5;
      sparklesRef.current.children.forEach((child, i) => {
        child.position.y = Math.sin(state.clock.elapsedTime * 2 + i) * 0.5;
      });
    }
  });

  return (
    <group ref={sparklesRef}>
      {Array.from({ length: count }).map((_, i) => {
        const angle = (i / count) * Math.PI * 2;
        const radius = 2 + Math.random();
        return (
          <Sphere
            key={i}
            args={[0.05]}
            position={[
              Math.cos(angle) * radius,
              Math.random() * 2 - 1,
              Math.sin(angle) * radius
            ]}
          >
            <meshStandardMaterial
              color="#fbbf24"
              emissive="#fbbf24"
              emissiveIntensity={1}
            />
          </Sphere>
        );
      })}
    </group>
  );
};

// Main component with model loading
const ExerciseVisual3DModels = ({ exerciseName, step, stepText, animationData, onStepChange, totalSteps, className = "" }) => {
  const handlePrevious = () => {
    if (step > 0) onStepChange(step - 1);
  };

  const handleNext = () => {
    if (step < totalSteps - 1) onStepChange(step + 1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, totalSteps]);

  let touchStartX = 0;
  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }
  };

  return (
    <div className={`relative ${className}`}>
      <div 
        className="h-80 sm:h-96 bg-gradient-to-b from-sky-100 to-green-50 rounded-xl overflow-hidden shadow-inner"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Canvas shadows camera={{ position: [5, 4, 6], fov: 50 }}>
          <ambientLight intensity={0.6} />
          <directionalLight
            castShadow
            position={[10, 10, 5]}
            intensity={1}
            shadow-mapSize={[1024, 1024]}
          />
          <pointLight position={[-10, -10, -10]} intensity={0.4} />
          <spotLight
            castShadow
            position={[0, 10, 0]}
            angle={0.3}
            penumbra={1}
            intensity={0.5}
          />
          
          <Suspense fallback={<LoadingFallback />}>
            <ModelAnimationLogger />
            <ExerciseSceneWithModels 
              exerciseName={exerciseName} 
              step={step} 
              stepText={stepText}
              animationData={animationData}
            />
          </Suspense>
          
          {/* Ground */}
          <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.5, 0]} receiveShadow>
            <planeGeometry args={[20, 20]} />
            <meshStandardMaterial color="#90EE90" />
          </mesh>
          
          <ContactShadows 
            opacity={0.4}
            scale={10}
            blur={1.5}
            far={4.5}
            resolution={256}
            color="#000000"
          />
          
          <OrbitControls 
            enablePan={false} 
            enableZoom={false} 
            minPolarAngle={Math.PI / 4}
            maxPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </div>
      
      {/* Navigation Controls */}
      <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center space-x-4">
        <button
          onClick={handlePrevious}
          disabled={step === 0}
          className="bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg disabled:opacity-50 hover:bg-white transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        
        <div className="flex space-x-2">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <button
              key={index}
              onClick={() => onStepChange(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === step ? 'bg-blue-600 w-8' : 'bg-gray-400'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={handleNext}
          disabled={step === totalSteps - 1}
          className="bg-white/80 backdrop-blur-sm p-2 rounded-full shadow-lg disabled:opacity-50 hover:bg-white transition-all"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* Step Counter */}
      <div className="absolute top-4 right-4 bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full shadow-lg">
        <span className="text-sm font-medium text-gray-700">
          Step {step + 1} of {totalSteps}
        </span>
      </div>
    </div>
  );
};

export default ExerciseVisual3DModels;

// Preload models
useGLTF.preload('/models/ShibaInu.gltf');
useGLTF.preload('/models/Character_Male_1.gltf');